import { MapMode } from 'app/agriculture/contexts/MapModeContextProvider';
import {
    MapVisibilityContext,
    ToggleableMapVisibility,
} from 'app/agriculture/contexts/MapVisibilityContextProvider';
import { useCallback, useContext } from 'react';
import { EntityType } from 'app/agriculture/models/Entities';

export function useMapVisibility(): {
    isVisible: (typeName: EntityType | 'all') => boolean;
    toggleVisibility: (typeName: EntityType | 'all', show: boolean) => void;
    toggleMapModeFiltering: (filter: keyof typeof MapMode) => void;
} {
    const { storedVisibility, setStoredVisibility } = useContext(MapVisibilityContext);
    function modifyVisibleStore(entityTypesToAdd: EntityType[], entityTypesToRemove: EntityType[]) {
        setStoredVisibility((storedVisibility) =>
            Array.from(
                new Set([
                    ...storedVisibility.filter((x) => !entityTypesToRemove.includes(x)),
                    ...entityTypesToAdd,
                ]),
            ),
        );
    }

    function toggleVisibility(typeName: EntityType | 'all', show: boolean) {
        if (typeName == 'all') {
            if (show) {
                modifyVisibleStore(ToggleableMapVisibility, []);
            } else {
                modifyVisibleStore([], ToggleableMapVisibility);
            }
        } else {
            if (show) {
                modifyVisibleStore([typeName], []);
            } else {
                modifyVisibleStore([], [typeName]);
            }
        }
    }

    // eslint-disable-next-line complexity
    function toggleMapModeFiltering(filter: keyof typeof MapMode) {
        switch (filter) {
            case 'Overview':
                modifyVisibleStore([], ['Task', 'Herd', 'Note', 'Device']);
                break;
            case 'Notes':
                modifyVisibleStore(['Note'], ['Task', 'Herd', 'Device']);
                break;
            case 'Tasks':
                modifyVisibleStore(['Task'], ['Note', 'Herd', 'Device']);
                break;
            case 'Herds':
                modifyVisibleStore(['Herd'], ['Note', 'Task', 'Device']);
                break;
            case 'Devices':
                modifyVisibleStore(['Device'], ['Note', 'Task', 'Herd']);
                break;
            case 'Search':
                break;
        }
    }

    const isVisible = useCallback(
        (typeName: EntityType | 'all') => {
            if (typeName == 'all') {
                return ToggleableMapVisibility.every((val) => storedVisibility.includes(val));
            } else {
                return storedVisibility.includes(typeName);
            }
        },
        [storedVisibility],
    );

    return { isVisible, toggleVisibility, toggleMapModeFiltering };
}
